import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import handlePromise from 'utils/Promise';
import AdminInvitationsServices from 'services/api/AdminInvitations';
import { InvitationDocumentsPayload } from 'utils/invitationDocuments';

export type ValidateDocumentsEstado = 'idle' | 'processing' | 'completed';

export type ValidateDocumentoItem = {
  nombre: string;
  urlSharepoint?: string;
  estado: string;
  detalle?: string;
};

export type ValidateDocumentsResumen = {
  validos: number;
  advertencias?: number;
  invalidos: number;
  puedeEnviar: boolean;
};

const POLL_INTERVAL_MS = 4000;

type ValidateDocumentsApiPayload = {
  validacionId?: string;
  ValidacionId?: string;
  estado?: string;
  Estado?: string;
  progreso?: number;
  Progreso?: number;
  total?: number;
  Total?: number;
  error?: string;
  Error?: string;
  errorDetail?: string;
  ErrorDetail?: string;
  resumen?: {
    validos?: number;
    Validos?: number;
    invalidos?: number;
    Invalidos?: number;
    advertencias?: number;
    Advertencias?: number;
    puedeEnviar?: boolean;
    PuedeEnviar?: boolean;
  };
  Resumen?: {
    validos?: number;
    Validos?: number;
    invalidos?: number;
    Invalidos?: number;
    advertencias?: number;
    Advertencias?: number;
    puedeEnviar?: boolean;
    PuedeEnviar?: boolean;
  };
  documentos?: Array<{
    nombre?: string;
    Nombre?: string;
    urlSharepoint?: string;
    UrlSharepoint?: string;
    estado?: string;
    Estado?: string;
    detalle?: string;
    Detalle?: string;
  }>;
  Documentos?: Array<{
    nombre?: string;
    Nombre?: string;
    urlSharepoint?: string;
    UrlSharepoint?: string;
    estado?: string;
    Estado?: string;
    detalle?: string;
    Detalle?: string;
  }>;
};

const ERROR_KEYS = [
  'VALIDACION_DOCUMENTOS_EN_CURSO',
  'VALIDACION_DOCUMENTOS_SIN_DOCS',
  'VALIDACION_DOCUMENTOS_CON_ERRORES',
  'VALIDACION_DOCUMENTOS_SERVICE_UNAVAILABLE',
] as const;

const FALLBACK_KEY = 'VALIDACION_DOCUMENTOS_SERVICE_UNAVAILABLE';

const DOC_STATUS_KEYS = [
  'OK',
  'WARNING_WATERMARK',
  'ERROR_SHAREPOINT',
  'ERROR_EMPTY',
  'ERROR_CORRUPT',
] as const;

const LOG_PREFIX = '[ValidarDocs]';

function readResumen( data: ValidateDocumentsApiPayload['resumen'] | ValidateDocumentsApiPayload['Resumen'] ) {
  if ( !data ) return null;
  return {
    validos: data.validos ?? data.Validos ?? 0,
    advertencias: data.advertencias ?? data.Advertencias ?? 0,
    invalidos: data.invalidos ?? data.Invalidos ?? 0,
    puedeEnviar: data.puedeEnviar ?? data.PuedeEnviar ?? false,
  };
}

function readDocumentos( data: ValidateDocumentsApiPayload ) {
  const list = data.documentos ?? data.Documentos ?? [];
  return list.map( ( item ) => ( {
    nombre: item.nombre ?? item.Nombre ?? '',
    urlSharepoint: item.urlSharepoint ?? item.UrlSharepoint,
    estado: item.estado ?? item.Estado ?? '',
    detalle: item.detalle ?? item.Detalle,
  } ) );
}

function readStatusPayload( data: ValidateDocumentsApiPayload | null | undefined ) {
  if ( !data ) return null;
  return {
    estado: data.estado ?? data.Estado,
    progreso: data.progreso ?? data.Progreso ?? 0,
    total: data.total ?? data.Total ?? 0,
    error: data.error ?? data.Error,
    errorDetail: data.errorDetail ?? data.ErrorDetail,
    resumen: readResumen( data.resumen ?? data.Resumen ),
    documentos: readDocumentos( data ),
  };
}

function isKnownErrorKey( key: string ): boolean {
  return ( ERROR_KEYS as readonly string[] ).includes( key );
}

const useValidateDocuments = () => {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<ValidateDocumentsEstado>( 'idle' );
  const [progreso, setProgreso] = useState( 0 );
  const [total, setTotal] = useState( 0 );
  const [documentos, setDocumentos] = useState<ValidateDocumentoItem[]>( [] );
  const [resumen, setResumen] = useState<ValidateDocumentsResumen | null>( null );
  const [validacionId, setValidacionId] = useState<string | null>( null );
  const [errorMessage, setErrorMessage] = useState<string | null>( null );

  const validacionIdRef = useRef<string | null>( null );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>( null );
  const mountedRef = useRef( true );

  const stopPolling = useCallback( () => {
    if ( intervalRef.current ) {
      clearInterval( intervalRef.current );
      intervalRef.current = null;
    }
  }, [] );

  useEffect( () => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling] );

  const showError = useCallback( ( key: string, extra?: Record<string, unknown> ) => {
    const i18nKey = isKnownErrorKey( key ) ? key : FALLBACK_KEY;
    const message = t( `admin.invitations.validateDocuments.errors.${i18nKey}` );
    // eslint-disable-next-line no-console
    console.error( LOG_PREFIX, 'ERROR', { key, message, extra } );
    setEstado( 'idle' );
    setErrorMessage( message );
  }, [t] );

  const poll = useCallback( () => {
    if ( !validacionIdRef.current || !mountedRef.current ) return;

    const doPoll = async () => {
      const [error, response, data] = await handlePromise(
        AdminInvitationsServices.getValidateDocumentsStatus( validacionIdRef.current! ),
      );

      // eslint-disable-next-line no-console
      console.log( LOG_PREFIX, 'poll', {
        validacionId: validacionIdRef.current,
        ok: response?.ok,
        status: response?.status,
        error,
        raw: data,
      } );

      if ( !mountedRef.current ) return;

      if ( !response?.ok ) {
        stopPolling();
        showError( 'VALIDACION_DOCUMENTOS_SERVICE_UNAVAILABLE', { status: response?.status, error, raw: data } );
        return;
      }

      const payload = readStatusPayload( data );
      // eslint-disable-next-line no-console
      console.log( LOG_PREFIX, 'poll parsed', payload );

      if ( payload?.estado === 'PROCESANDO' ) {
        setProgreso( payload.progreso );
        setTotal( payload.total );
        if ( payload.documentos.length > 0 ) {
          setDocumentos( payload.documentos );
        }
      } else if ( payload?.estado === 'COMPLETADO' ) {
        stopPolling();
        setProgreso( payload.total );
        setTotal( payload.total );
        setDocumentos( payload.documentos );
        setResumen( payload.resumen );
        setEstado( 'completed' );
        setErrorMessage( null );

        // eslint-disable-next-line no-console
        console.log( LOG_PREFIX, 'COMPLETADO', {
          resumen: payload.resumen,
          documentos: payload.documentos.length,
        } );
      } else if ( payload?.estado === 'ERROR' ) {
        stopPolling();
        showError( payload.error || FALLBACK_KEY, {
          errorDetail: payload.errorDetail,
        } );
      }
    };

    doPoll();
    intervalRef.current = setInterval( doPoll, POLL_INTERVAL_MS );
  }, [stopPolling, showError] );

  const resetValidation = useCallback( () => {
    stopPolling();
    validacionIdRef.current = null;
    setValidacionId( null );
    setEstado( 'idle' );
    setProgreso( 0 );
    setTotal( 0 );
    setDocumentos( [] );
    setResumen( null );
    setErrorMessage( null );
  }, [stopPolling] );

  const startValidation = useCallback( async ( payload: InvitationDocumentsPayload ) => {
    stopPolling();
    setEstado( 'processing' );
    setProgreso( 0 );
    setTotal( 0 );
    setDocumentos( [] );
    setResumen( null );
    setErrorMessage( null );
    setValidacionId( null );
    validacionIdRef.current = null;

    const body = {
      carpetasPaths: payload.carpetasPaths,
      documentosIds: payload.documentosIds,
      type: 'PERSONAL',
    };

    // eslint-disable-next-line no-console
    console.log( LOG_PREFIX, 'start', body );

    const [error, response, data] = await handlePromise(
      AdminInvitationsServices.startValidateDocuments( body ),
    );

    // eslint-disable-next-line no-console
    console.log( LOG_PREFIX, 'start response', {
      ok: response?.ok,
      status: response?.status,
      error,
      raw: data,
    } );

    if ( !mountedRef.current ) return;

    if ( !response?.ok ) {
      const key = ( typeof error === 'string' && error ) || FALLBACK_KEY;
      showError( key, { status: response?.status, error, raw: data } );
      return;
    }

    const startPayload = data as ValidateDocumentsApiPayload;
    const id = startPayload.validacionId ?? startPayload.ValidacionId;
    if ( !id ) {
      showError( FALLBACK_KEY, data as Record<string, unknown> );
      return;
    }

    validacionIdRef.current = id;
    setValidacionId( id );
    setTotal( startPayload.total ?? startPayload.Total ?? 0 );
    poll();
  }, [stopPolling, showError, poll] );

  const translateDocStatus = useCallback( ( status: string ) => {
    if ( status === 'OK' ) {
      return t( 'admin.invitations.validateDocuments.docStatus.OK' );
    }
    if ( ( DOC_STATUS_KEYS as readonly string[] ).includes( status ) ) {
      return t( `admin.invitations.validateDocuments.docStatus.${status}` );
    }
    return status;
  }, [t] );

  const puedeEnviar = estado === 'completed' && resumen?.puedeEnviar === true;

  return {
    estado,
    progreso,
    total,
    documentos,
    resumen,
    validacionId,
    errorMessage,
    puedeEnviar,
    startValidation,
    resetValidation,
    translateDocStatus,
  };
};

export default useValidateDocuments;
